/**
 * Generation Results
 *
 * Display generated images and captions.
 */

import { Download, Copy, Check, Image as ImageIcon, Hash } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/v2/ui';
import type { GeneratedImage, GeneratedCaption } from '@/lib/api/content-studio';

interface GenerationResultsProps {
  images: GeneratedImage[];
  captions: Record<string, GeneratedCaption>;
}

export function GenerationResults({ images, captions }: GenerationResultsProps) {
  const { t } = useTranslation();
  const [copiedCaption, setCopiedCaption] = useState<string | null>(null);

  const handleCopyCaption = async (style: string, caption: GeneratedCaption) => {
    const text = `${caption.caption}\n\n${caption.hashtags.map((h) => `#${h}`).join(' ')}\n\n${caption.cta}`;
    await navigator.clipboard.writeText(text);
    setCopiedCaption(style);
    setTimeout(() => setCopiedCaption(null), 2000);
  };

  const formatLabels: Record<string, string> = {
    instagram_feed: 'Instagram Feed',
    instagram_story: 'Instagram Story',
    facebook_post: 'Facebook Post',
    facebook_story: 'Facebook Story',
    carousel_slide: 'Carousel Slide',
    pinterest_pin: 'Pinterest Pin',
    linkedin_post: 'LinkedIn Post',
  };

  const styleLabels: Record<string, string> = {
    emotional: 'Emotional',
    factual: 'Faktenbasiert',
    humorous: 'Humorvoll',
    urgency: 'Dringlichkeit',
    storytelling: 'Storytelling',
  };

  return (
    <div className="space-y-6">
      {/* Generated Images */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Social-Media-Bilder
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image) => (
              <div
                key={image.image_id || image.s3_key}
                className="relative group rounded-lg overflow-hidden border"
              >
                <img
                  src={image.image_url || `/api/image/${image.s3_key}`}
                  alt={formatLabels[image.format] || image.format}
                  className="w-full aspect-square object-cover"
                />

                {/* Format Badge */}
                <Badge className="absolute bottom-2 left-2 bg-black/70 text-white text-xs">
                  {formatLabels[image.format] || image.format}
                </Badge>

                {/* Size Badge */}
                <Badge
                  variant="default"
                  className="absolute bottom-2 right-2 bg-white/90 text-xs"
                >
                  {image.width}x{image.height}
                </Badge>

                {/* Download Button (on hover) */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      const downloadUrl = image.image_url || `/api/image/${image.s3_key}?download=1`;
                      window.open(downloadUrl, '_blank');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Generated Captions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Hash className="h-5 w-5" />
            Social-Content
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={Object.keys(captions)[0]} className="w-full">
            <TabsList className="mb-4">
              {Object.keys(captions).map((style) => (
                <TabsTrigger key={style} value={style}>
                  {styleLabels[style] || style}
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.entries(captions).map(([style, caption]) => (
              <TabsContent key={style} value={style} className="space-y-4">
                {/* Caption Text */}
                <div className="p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800">
                  <p className="text-sm whitespace-pre-wrap">{caption.caption}</p>
                </div>

                {/* Hashtags */}
                <div className="flex flex-wrap gap-2">
                  {caption.hashtags.map((hashtag) => (
                    <Badge
                      key={hashtag}
                      variant="secondary"
                      className="text-xs"
                    >
                      #{hashtag}
                    </Badge>
                  ))}
                </div>

                {/* CTA */}
                {caption.cta && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-neutral-500">CTA:</span>
                    <Badge variant="default">{caption.cta}</Badge>
                  </div>
                )}

                {/* Copy Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyCaption(style, caption)}
                >
                  {copiedCaption === style ? (
                    <>
                      <Check className="h-4 w-4 mr-2 text-green-500" />
                      {t('common.copied')}
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Caption kopieren
                    </>
                  )}
                </Button>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
